import { useState } from 'react';

import { usePatchIncident, type Incident } from '../api';

/**
 * Command and comms for this activation — the fields the packet's info page
 * prints.
 *
 * These used to exist only as rules on paper, filled in with a pen, which
 * meant the one set of facts everybody needs ("what channel are we on, who is
 * commander") survived exactly as long as the sheet did and was lost on a
 * reprint. Recording them here is the same move sector assignments made.
 *
 * Deliberately one flat form rather than per-field editing: these get filled
 * in once, at the start, in a single sitting — usually by somebody reading
 * them off a whiteboard.
 */
const FIELDS: { key: keyof Incident; label: string; placeholder: string }[] = [
  { key: 'commander', label: 'Commander', placeholder: 'who is running this' },
  { key: 'commander_phone', label: 'Phone', placeholder: '(801) 555-0100' },
  { key: 'deputy', label: 'Deputy / scribe', placeholder: 'who is keeping the log' },
  { key: 'deputy_phone', label: 'Phone', placeholder: '' },
  { key: 'staging_area', label: 'Staging', placeholder: 'where runners muster' },
  { key: 'radio_primary', label: 'Radio primary', placeholder: '146.520 MHz simplex' },
  { key: 'radio_alt', label: 'Alternate', placeholder: 'where the net moves' },
  { key: 'frs_channel', label: 'FRS / GMRS', placeholder: 'channel 3, privacy 0' },
  { key: 'net_schedule', label: 'Net', placeholder: 'top of the hour' },
];

export default function CommandPanel({ incident }: { incident: Incident }) {
  const patch = usePatchIncident();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Partial<Incident>>({});

  const value = (k: keyof Incident) =>
    (draft[k] as string | undefined) ?? ((incident[k] as string | null) ?? '');

  const filled = FIELDS.filter((f) => (incident[f.key] as string | null)?.trim()).length;

  const save = () => {
    const body: Record<string, string | null> = {};
    for (const f of FIELDS) {
      const v = (draft[f.key] as string | undefined);
      if (v !== undefined) body[f.key] = v.trim() || null;
    }
    if (!Object.keys(body).length) { setOpen(false); return; }
    patch.mutate({ id: incident.id, body: body as Partial<Incident> },
                 { onSuccess: () => { setDraft({}); setOpen(false); } });
  };

  return (
    <section className="rounded border border-slate-200 dark:border-slate-700 print:hidden">
      <header className="flex items-center justify-between border-b border-slate-200 px-3 py-2 dark:border-slate-700">
        <h2 className="text-sm font-semibold">Command and comms</h2>
        <span className="flex items-center gap-2 text-xs">
          <span className="text-slate-400">{filled} of {FIELDS.length}</span>
          <button onClick={() => setOpen(!open)}
                  className="text-sky-700 hover:underline dark:text-sky-400">
            {open ? 'Close' : filled ? 'Edit' : 'Fill in'}
          </button>
        </span>
      </header>

      {!open && (
        <dl className="grid grid-cols-[7rem_1fr] gap-x-2 gap-y-1 p-3 text-xs">
          {FIELDS.filter((f) => (incident[f.key] as string | null)?.trim()).map((f) => (
            <div key={f.key} className="contents">
              <dt className="text-slate-500 dark:text-slate-400">{f.label}</dt>
              <dd className="font-medium">{incident[f.key] as string}</dd>
            </div>
          ))}
          {filled === 0 && (
            <p className="col-span-2 text-slate-500 dark:text-slate-400">
              Nothing recorded. The packet prints these as blanks to fill in with a pen.
            </p>
          )}
        </dl>
      )}

      {open && (
        <form
          onSubmit={(e) => { e.preventDefault(); save(); }}
          className="space-y-2 p-3 text-xs"
        >
          {FIELDS.map((f) => (
            <label key={f.key} className="flex items-center gap-2">
              <span className="w-24 shrink-0 text-slate-500 dark:text-slate-400">{f.label}</span>
              <input
                value={value(f.key)}
                placeholder={f.placeholder}
                onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                className="flex-1 rounded border border-slate-300 bg-white px-1.5 py-0.5 dark:border-slate-600 dark:bg-slate-800"
              />
            </label>
          ))}
          <label className="flex items-start gap-2">
            <span className="w-24 shrink-0 pt-1 text-slate-500 dark:text-slate-400">Notes</span>
            <textarea
              rows={2}
              value={value('comms_notes')}
              placeholder="anything else the info page should carry"
              onChange={(e) => setDraft((d) => ({ ...d, comms_notes: e.target.value }))}
              className="flex-1 rounded border border-slate-300 bg-white px-1.5 py-0.5 dark:border-slate-600 dark:bg-slate-800"
            />
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => { setDraft({}); setOpen(false); }}
                    className="px-2 py-0.5 text-slate-500">
              Cancel
            </button>
            <button type="submit" disabled={patch.isPending}
                    className="rounded bg-slate-800 px-3 py-1 text-white dark:bg-slate-200 dark:text-slate-900">
              {patch.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
